<template>
    <div>
        <v-progress-linear v-if="!ajaxDone" :indeterminate="true"></v-progress-linear>
        <div v-if="ajaxErrorMessage">
            <v-alert :value="true" color="warning" icon="warning" v-if="!server || !fellowshipId || fellowshipId === 'undefined' || fellowshipId === 'null'">
                {{ $t('site.youAreNotInAFellowship') }}
            </v-alert>
            <v-alert :value="true" color="error" icon="warning" v-else>
                {{ajaxErrorMessage}}
            </v-alert>
        </div>
        <div v-if="ajaxDone && !ajaxErrorMessage">
            <table>
                <tr>
                    <td>
                        <elv-banner-icon height="80px" width="80px" :banner="fellowship.banner"></elv-banner-icon>
                    </td>
                    <td>&nbsp;</td>
                    <td>
                        <h1>{{ fellowship.fellowship }}</h1>
                        <h2 class="subtitle">{{ fellowship.serverName }}</h2>
                    </td>
                </tr>
            </table>

            <div class="row">
                <div class="col-md-2">
                    <div class="panel">
                        <div class="panel-content">
                            <h1 style="text-align:center;">{{ fellowship.rank }}</h1>
                        </div>
                        <div class="panel-footer bg-dark"><h3><strong>{{ $t('site.fellowship.stats.rank') }}</strong></h3></div>
                    </div>
                </div>
                <div class="col-md-2">
                    <div class="panel">
                        <div class="panel-content">
                            <h1 style="text-align:center;">
                                <vue-numeric :value="fellowship.currScore"/>
                            </h1>
                        </div>
                        <div class="panel-footer bg-dark"><h3><strong>{{ $t('site.common.score') }}</strong></h3></div>
                    </div>
                </div>
                <div class="col-md-2">
                    <div class="panel">
                        <div class="panel-content">
                            <h1 style="text-align:center;">
                                <vue-numeric :value="fellowship.medianScore"/>
                            </h1>
                        </div>
                        <div class="panel-footer bg-dark"><h3 v-html="this.$i18n.t('site.fellowship.stats.medianUserScore')"></h3></div>
                    </div>
                </div>
                <div class="col-md-2">
                    <div class="panel">
                        <div class="panel-content">
                            <h1 style="text-align:center;">{{ members.length }}</h1>
                        </div>
                        <div class="panel-footer bg-dark"><h3 v-html="this.$i18n.t('site.fellowship.stats.memberCount')"></h3></div>
                    </div>
                </div>
                <div class="col-md-2">
                    <div class="panel">
                        <div class="panel-content" style="max-height:70px;">
                            <h1 style="text-align:center;" :title="$t('site.averageChests4WTip')">{{ fellowship.chestAverage < 10 ? fellowship.chestAverage.toFixed(1) : 10 }} x <img src="/static/img/icons/chests.png" width="32"></h1>
                        </div>
                        <div class="panel-footer bg-dark"><h3 v-html="this.$i18n.t('site.tournamentAverage4W')" :title="$t('site.averageChests4WTip')"></h3></div>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-lg-6">
                    <div class="panel">
                        <highcharts :options="scoreHistoryChart"></highcharts>
                    </div>
                </div>
                <div class="col-lg-6">
                    <div class="panel">
                        <highcharts :options="tournamentHistoryChart"></highcharts>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-sm-4">
                    <div class="panel">
                        <highcharts :options="tier1"></highcharts>
                    </div>
                </div>
                <div class="col-sm-4">
                    <div class="panel">
                        <highcharts :options="tier2"></highcharts>
                    </div>
                </div>
                <div class="col-sm-4">
                    <div class="panel">
                        <highcharts :options="tier3"></highcharts>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-sm-4">
                    <div class="panel">
                        <div class="panel-content" style="margin-top:-25px">
                            <h2>{{$t("site.fellowship.idealCandidate.heading")}}</h2>
                            <span v-html="$t('site.fellowship.idealCandidate.description', {medianScore: '<strong>' + fellowship.medianScore + '</strong>', boosts: fellowship && fellowship.boostsNeeded && fellowship.boostsNeeded.join(', ') || 'Unknown' })"></span>
                            <span v-if="fellowship.boostsNeeded.length >= 2">
                                <small>
                                    <i18n path="site.fellowship.idealCandidate.note" tag="span">
                                        <router-link to="/faq#boostCombinations">{{$t('site.common.forMoreInfo')}}</router-link>
                                    </i18n>
                                </small>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <hr>
            <h2>{{$t('site.fellowship.memberGoodsTable.heading')}}</h2>
            <small>{{$t('site.fellowship.memberGoodsTable.description')}}
            </small>

            <div>
                <center>
                    <button @click="refreshFellowship" class="btn btn-primary" :title="$t('site.fellowship.memberGoodsTable.refreshButtonTip')">
                        {{$t('site.fellowship.memberGoodsTable.refreshButtonText')}}
                    </button>
                </center>
            </div>
            <div v-if="refreshingUsersInProgress" class="row">
                <v-alert :value="true" :color="refreshUsersColor" :icon="refreshUsersIcon" style="color: black; width:600px"
                         transition="scale-transition">
                    <span><strong>{{refreshUsersHeadline}}</strong></span>
                    <v-progress-linear :value="refreshProgressValue"></v-progress-linear>
                    <textarea v-model="refreshProgressLog" rows=3 style="width:100%;margin-left:auto;margin-right:auto">
                    </textarea>
                </v-alert>
            </div>

            <div class="row">
                <div class="col-lg-12">
                    <div class="panel">
                        <v-data-table
                                ref="goodsTable"
                                :headers="headers"
                                :items="members"
                                hide-actions
                                class="elevation-1"
                        >

                            <template slot="headerCell" slot-scope="props">
                                <v-tooltip top max-width="400">
                                    <span slot="activator">
                                      {{ props.header.text }}
                                    </span>
                                    <span v-html="props.header.tooltip || props.header.text"></span>
                                </v-tooltip>
                            </template>
                            <template slot="items" slot-scope="props">
                                <td>
                                    <elv-avatar-icon :avatar="props.item.avatar" :isFriend="props.item.isFriend" width="32"/>
                                </td>
                                <td>
                                    <router-link :to="'/player/'+ props.item.server + '/' + props.item.playerId">
                                        {{ props.item.username }}
                                    </router-link>
                                    <elv-user-badges :player="props.item"></elv-user-badges>
                                </td>
                                <td>{{ props.item.currScore }}</td>
                                <td>{{  props.item.fellowshipRole && $t( `site.common[ '${props.item.fellowshipRole}']`) }}</td>
                                <td>{{ props.item.technologySection }}</td>
                                <td>
                                    <elv-boost-icon :boost="props.item.boost1"
                                                    :value="props.item.cityReport.production[ props.item.boost1 + 'Computed']"></elv-boost-icon>
                                </td>
                                <td>
                                    <elv-boost-icon :boost="props.item.boost2"
                                                    :value="props.item.cityReport.production[ props.item.boost2 + 'Computed']"></elv-boost-icon>
                                </td>
                                <td>
                                    <elv-boost-icon :boost="props.item.boost3"
                                                    :value="props.item.cityReport.production[ props.item.boost3 + 'Computed']"></elv-boost-icon>
                                </td>
                                <td>
                                    <elv-collects-per-day-input v-model="props.item.collectsPerDay"
                                                                :helpText="$t('site.fellowship.memberGoodsTable.collectsPerDayValidation')"
                                                                :playerId="props.item.playerId"
                                                                fieldName="collectsPerDay"
                                                                :suffix="$t('site.fellowship.memberGoodsTable.perDayShort')"
                                    ></elv-collects-per-day-input>
                                </td>
                                <td>
                                    <elv-collects-per-day-input v-model="props.item.boost1Percent" :playerId="props.item.playerId"
                                                                fieldName="boost1Percent" size=3 :min="boostMin" :max="boostMax"
                                                                suffix="%"
                                                                :helpText="$t('site.fellowship.memberGoodsTable.boostPercentValidation')"></elv-collects-per-day-input>
                                    <elv-collects-per-day-input v-model="props.item.boost2Percent" :playerId="props.item.playerId"
                                                                fieldName="boost2Percent" size=3 :min="boostMin" :max="boostMax"
                                                                suffix="%"
                                                                :helpText="$t('site.fellowship.memberGoodsTable.boostPercentValidation')"></elv-collects-per-day-input>
                                    <elv-collects-per-day-input v-model="props.item.boost3Percent" :playerId="props.item.playerId"
                                                                fieldName="boost3Percent" size=3 :min="boostMin" :max="boostMax"
                                                                suffix="%"
                                                                :helpText="$t('site.fellowship.memberGoodsTable.boostPercentValidation')"></elv-collects-per-day-input>
                                </td>
                                <td class="text-xs-center">
                                    {{ ((props.item.mountainHallBoostPercent||0)*100).toFixed(0) + "%"}}
                                </td>
                                <td>
                                    <elv-refresh-user-icon :player="props.item"></elv-refresh-user-icon>
                                </td>
                            </template>
                            <template slot="no-data">
                                <v-alert :value="true" color="error" icon="warning">
                                    Sorry, nothing to display here :(
                                </v-alert>
                            </template>
                        </v-data-table>
                        <center>
                            <button @click="saveGoodsTableChanges" v-show="isUnsavedItems" class="btn btn-primary">
                                {{ $t('site.common.saveChanges') }}
                            </button>
                        </center>
                    </div>
                </div>
            </div>

            <h2>{{ $t('site.fellowship.coinsAndSuppliesTable.heading') }}</h2>
            <small>{{ $t('site.fellowship.coinsAndSuppliesTable.description') }}
            </small>
            <elv-other-production-table :members="members" :showToggles="true"></elv-other-production-table>

            <h2>{{ $t('site.fellowship.popCultureTable.heading') }}</h2>
            <small>{{ $t('site.fellowship.popCultureTable.description') }}
            </small>
            <elv-pop-culture-table :members="members"></elv-pop-culture-table>

            <h2>{{ $t('site.fellowship.cityCoverageTable.heading') }}</h2>
            <small>{{ $t('site.fellowship.cityCoverageTable.description') }}</small>
            <elv-tiles-table :members="members"></elv-tiles-table>

            <h2>{{ $t('site.fellowship.weeklyReport.heading') }}</h2>
            <small>{{ $t('site.fellowship.weeklyReport.description') }}
            </small>
            <div class="row">
                <div class="col-sm-6">
                    <div class="panel">
                        <div class="panel-content">

                            <textarea v-model="weeklyReport" style="width:100%;height:600px;">
                                        </textarea>

                            <table>
                                <tr>
                                    <td>
                                        <button type="button" class="btn btn-primary btn-sm"
                                                v-clipboard:copy="weeklyReport"
                                                v-clipboard:success="onCopy"
                                                v-clipboard:error="onError"><i class="fas fa-clipboard"></i> &nbsp;{{ $t('site.common.copy') }}
                                        </button>
                                    </td>
                                    <td>
                                    <td>
                                        <span v-bind:class="{ red: weeklyReportLength > 2000}">{{weeklyReportLength}} / 2000</span>
                                    </td>
                                    <td>&nbsp;&nbsp;&nbsp;</td>

                                    <td>
                                        <!--<v-flex xs6>-->
                                            <v-switch
                                                :title="this.$i18n.t('site.makeReportFitInOneMessageTip')"
                                                style="background-color: rgba(0,0,0,0); width:400px; margin-bottom: -15px; margin-top: 10px; vertical-align: center"
                                                :label="`${ $t('site.makeReportFitInOneMessage')}: ${ makeReportFitInOneMessage ? $t('site.common.yes'): $t('site.common.no')}`"
                                                v-model="makeReportFitInOneMessage"
                                            ></v-switch>
                                        <!--</v-flex>-->
                                    </td>
                                </tr>
                            </table>
                        </div>


                    </div>
                </div>
                <div class="col-sm-6">
                    <v-select
                            :items="reportDates"
                            v-model="reportDate"
                            label="Select Report Date"
                            single-line
                            dense
                            :hint="$t('site.fellowship.weeklyReport.selectReportHint')"
                            persistent-hint
                    ></v-select>
                    <img src="../assets/weeklyReportExample.png" width="600"
                         style="line-height:100%;margin-top: 10px; margin-bottom: 10px">
                    <div>
                        <v-alert :value="true" color="info" icon="info" outline>
                            {{ $t('site.fellowship.weeklyReport.reportsAreOptimizedFor') }}
                        </v-alert>
                    </div>
                </div>
            </div>

            <h2>{{ $t('site.fellowship.memberHistory.heading') }}</h2>
            <div class="row">
                <div class="col-lg-12">
                    <div class="panel">
                        <v-data-table :items="fellowship.memberHistory"
                                      :headers="memberHistoryHeaders"
                                      hide-actions
                                      class="elevation-1"
                        >
                            <template slot="items" slot-scope="props">
                                <td>{{props.item.username}}</td>
                                <td>{{ $t( `site.common[ '${props.item.action}']`) }}</td>
                                <td>{{props.item.date | yyyymmdd }}</td>
                            </template>
                            <template slot="no-data">
                                <v-alert :value="true" color="info" icon="warning">
                                    {{ $t('site.fellowship.memberHistory.noChangesDetected', fellowship.createdAt) }}
                                </v-alert>
                            </template>
                        </v-data-table>
                    </div>
                </div>
            </div>
        </div>

        <v-snackbar
                :timeout="2400000"
                color="error"
                :multi-line="true"
                v-model="refreshNeededError"
        >
            {{refreshNeededHeadline}}.
            <v-btn dark flat @click.native="refreshNeededError = false">OK</v-btn>
        </v-snackbar>

        <v-snackbar
                :timeout="1000000"
                color=""
                :multi-line="true"
                v-model="saveSnackbar"
        >
            {{ $t('site.fellowship.notifications.unsavedChanges') }}
            <v-btn dark flat @click.native="saveGoodsTableChanges(); saveSnackbar = false"> {{ $t('site.common.saveChanges') | allCaps }}</v-btn>
        </v-snackbar>


    </div>
</template>

<script>
    import Vue from 'vue';
    import VueHighcharts from 'vue-highcharts';
    import Highcharts from 'highcharts';
    import loadDrilldown from 'highcharts/modules/drilldown';
    import VueNumeric from 'vue-numeric'
    import VueClipboard2 from 'vue-clipboard2';
    import ElvBoostIcon from '@/components/ElvBoostIcon';
    import RelativeTimeFormatter from '@/components/RelativeTimeFormatter';
    import _ from 'lodash';

    let elvenStats = require('../lib/elven-stats');
    let elvenCharts = require('../lib/elven-charts');
    let elvenWeeklyReport = require('../lib/elven-weekly-report');
    let testlib = require('../lib/testlib');
    import axios from 'axios';
    import { getHighchartLanguageOptions } from "@/main";

    Vue.use(VueHighcharts, {Highcharts: Highcharts});
    loadDrilldown(Highcharts)
    import ElvAvatarIcon from "../components/ElvAvatarIcon"
    import ElvPopCultureTable from "../components/ElvPopCultureTable"
    import ElvOtherProductionTable from "../components/ElvOtherProductionTable"
    import ElvRefreshUserIcon from "../components/ElvRefreshUserIcon"
    import ElvCollectsPerDayInput from "@/components/ElvUserEditableInput"
    import ElvCheckBackWhenMoreInfo from "@/components/ElvCheckBackWhenMoreInfo"
    import ElvTilesTable from "@/components/ElvTilesTable"
    import ElvUserBadges from "@/components/ElvUserBadges";
    import ElvBannerIcon from "@/components/ElvBannerIcon";

    Vue.use(VueClipboard2);

    export default {
        name: 'FellowshipDetail',
        props: ['fellowshipId', 'server'],
        components: {
            ElvBannerIcon,
            ElvUserBadges,
            ElvTilesTable,
            ElvCheckBackWhenMoreInfo,
            ElvCollectsPerDayInput,
            ElvRefreshUserIcon,
            ElvOtherProductionTable,
            ElvPopCultureTable,
            ElvAvatarIcon,
            ElvBoostIcon, VueNumeric, RelativeTimeFormatter
        },
        data() {
            return {
                isUnsavedItems: false,
                saveSnackbar: false,
                boostMin: 40,
                boostMax: 700,
                step: 1,
                ajaxDone: false,
                ajaxErrorMessage: "",
                fellowship: {},
                tier1: {},
                tier2: {},
                tier3: {},
                members: [],
                memberHistory: [],
                isInitialized: false,
                reportDates: [],
                reportDate: "",
                makeReportFitInOneMessage: true,
                refreshNeededError: false,
                refreshNeededHeadline: this.$i18n.t('site.fellowship.notifications.standbyImporting'),
                usersNeedingRefreshed: [],
                refreshingUsersInProgress: false,
                refreshAttempts: 0,
                refreshProgressLog: "INITIAL MESSAGE",
                refreshUsersHeadline: this.$i18n.t('site.fellowship.notifications.standbyImporting'),
                refreshUsersIcon: "warning",
                refreshUsersColor: "warning",
                refreshProgressValue: 0,
                optsInProgress: false,
                max25chars: (v) => v.length <= 25 || 'Input too long!',
                rules: {
                    required: (value) => !!value || 'Required.',
                    collectsPerDay: (value) => value <= 6 && value >= 1 || 'Must be 1 - 6',
                    boostPercentMax: (value) => value >= 700 || 'Must be < 700',
                    boostPercentMin: (value) => value < 100 || 'Must be > 100',
                }
            }
        },
        created() {
            console.log("FellowshipDetail Created");
            Event.$on("playerRefreshed", (data) => {
                if( data && data.playerId) {
                    console.error("Refreshed player: " + data.username);
                    let member = _.find(this.members, {playerId: data.playerId});

                    // I Cannot determine why this is needed here.  The extend is putting the cultureBoostPercent on the cityreport
                    // but once the members watch is triggered it is gone.  I think it must have something todo with it not being
                    // there when the object is first initialized and then not making its way into the watch... but the getters and such are
                    // here at this point.
                    // Also, playerrefreshed is being invoked twice.
                    elvenStats.initializeGoodsForUser(data); // Recompute the culture boost percent - TODO consider just setting boost percent, might retrigger tweens...
                    //console.log("Extending: " + data.username);
                    _.extend(member, data);
                    //console.log("Finished Extending: " + data.username);
                }
            });

            this.fetchData();
        },
        beforeRouteLeave (to, from , next) {
            if( this.isUnsavedItems ) {
                this.$i18n.t('site.')
                const answer = window.confirm( this.$i18n.t("site.confirmLeaveThereAreUnsavedChanges") );
                if (answer) {
                    next();
                } else {
                    next(false);
                }
            }else {
                next();
            }
        },
        watch: {
            '$route': 'fetchData',
            members: {
                handler(val) {
                    //if( this.isInitialized ) {
                    this.$ga.event('FellowshipGoods', 'RecomputeTriggered', "", "");
                    try {
                        console.log("Updating member productions...");
                        var tierData = elvenStats.computeGoodsBreakdownForFellowship(val);
                        //console.log("In Watch tierdata: ", tierData);
                        this.tier1 = createColumnOptions(tierData, "1", this.$i18n, this.Highcharts);
                        this.tier2 = createColumnOptions(tierData, "2", this.$i18n, this.Highcharts);
                        this.tier3 = createColumnOptions(tierData, "3", this.$i18n, this.Highcharts);


                        this.fellowship.boostsNeeded = _.map(tierData.boostsNeeded, (b) => { return "<strong>"+this.$i18n.t('site.common.' + b) + "</strong>"});

                        this.isUnsavedItems = _.size(this.getDirtyElements()) > 0;
                        this.saveSnackbar = _.size(this.getDirtyElements()) > 0;

                        //console.log("number of dirty elements:  " + _.size(this.getDirtyElements()));

                        //console.log("WATCH for members done");
                    }
                    catch (err) {
                        console.log("Failed to update goods", err);
                    }
                    // }
                },
                deep: true
            }
        },
        computed: {
            weeklyReport: {
                get: function () {
                    var str = "We need at least two weeks of data before we can generate a weekly report.  Here's a sample to give you an idea in the meantime!\n\n" +
                              elvenWeeklyReport.getSampleReport();
                    try {
                        //console.log("i18n here?",this.$i18n, this.$i18n.t('message.hello'));
                        str = elvenWeeklyReport.weeklyFellowshipReport({users: this.members, reportDate: this.reportDate, i18n: this.$i18n, makeReportFitInOneMessage: this.makeReportFitInOneMessage});
                    }
                    catch (err) {
                        console.error("Unable to create fellowship weekly report due to: ", err);
                    }
                    return str;
                }
            },
            weeklyReportLength: {
                get: function() {
                    return this.weeklyReport.length;
                }
            },
            memberHistoryHeaders: {
                get: function () {
                    return [
                        {text: this.$i18n.t('site.common.username'), value: "username", sortable: false},
                        {text: this.$i18n.t('site.common.action'), value: "action", sortable: false},
                        {text: this.$i18n.t('site.common.date'), value: "date", sortable: false},
                    ]
                }
            },
            headers: {
                get: function () {
                    try{
                        let cols = [
                        {text: "", value: 'avatar', sortable: false},
                        {
                            text: this.$i18n.t('site.common.username'),
                                align: 'left',
                            sortable: true,
                            value: 'username'
                        },
                        {text: this.$i18n.t('site.common.score'), value: 'currScore', tooltip: this.$i18n.t('site.fellowship.memberGoodsTable.scoreTip')},
                        {text: this.$i18n.t('site.common.role'), value: 'fellowshipRole', sortable: true},
                        {text: this.$i18n.t('site.common.chapter'), value: 'technologySection', tooltip: this.$i18n.t('site.fellowship.memberGoodsTable.chapterTip')},
                        {text: this.$i18n.t('site.common.t1'), value: 'boost1', tooltip: this.$i18n.t('site.fellowship.memberGoodsTable.boostTip')},
                        {text: this.$i18n.t('site.common.t2'), value: 'boost2', tooltip: this.$i18n.t('site.fellowship.memberGoodsTable.boostTip')},
                        {text: this.$i18n.t('site.common.t3'), value: 'boost3', tooltip: this.$i18n.t('site.fellowship.memberGoodsTable.boostTip')},
                        {text: this.$i18n.t('site.common.collectionsPerDay'), class: "colPerDayTh", width: 125, value: 'collectsPerDay', tooltip: this.$i18n.t('site.fellowship.memberGoodsTable.collectsPerDayTip')},
                        {text: this.$i18n.t('site.common.relicBoostPercent'), value: 'boost1Percent', tooltip: this.$i18n.t('site.fellowship.memberGoodsTable.relicBoostPercentTip')},
                        {text: this.$i18n.t('site.common.mountainHallPercentShort'), class: "mountainHallTh", value: 'mountainHallBoostPercent', tooltip: this.$i18n.t('site.fellowship.memberGoodsTable.mountainHallPercentTip')},
                        {text: this.$i18n.t('site.common.updated'), value: 'updatedDate', tooltip: this.$i18n.t('site.fellowship.memberGoodsTable.updatedTip')},
                        ];
                        return cols;
                    }
                    catch(err) {
                        console.error("ERROR building member goods columns: ", err);
                    }
                }
            },
        },
        methods: {
            fetchData: function () {
                axios.get(`/api/fellowship/${this.server}/${this.fellowshipId}`).then(response => {
                    this.loadFellowshipData(response);
                }).catch((err) => {
                    this.ajaxDone = true;
                    console.error("ERROR Fetching fellowship data", err);
                    this.ajaxErrorMessage = err.message || "Unknown Error Occurred loading fellowship.";
                });
            },
            loadFellowshipData: function (response) {
                //console.log("fellowship fetch", response);
                var users = _.get(response, "data.users");

                if (!_.size(users)) {
                    throw new Error("Unable to load fellowship, this may mean the fellowship has not yet been imported yet.");
                }
                this.usersNeedingRefreshed = _.map(_.filter(users, function (u) {
                    return !u.cityMapUpdatedAt;
                }), "username");
                this.refreshNeededError = _.size(this.usersNeedingRefreshed) > 0;
                if (this.refreshNeededError && this.refreshAttempts++ < 1) {
                    this.refreshFellowship();
                }


                elvenStats.initializeUsersGoods(users);

                this.members = _.sortBy(users, "currScore").reverse();
                try {
                    this.reportDates = elvenWeeklyReport.getPossibleReportDates(this.members);
                    this.reportDate = _.first(this.reportDates);
                }
                catch (err) {
                    console.error("error generating weekly report", err);
                }

                try {
                    this.fellowship = response.data.fellowship;
                    this.fellowship.serverName = elvenStats.mapDomainAndServerName(this.fellowship.server);
                    this.scoreHistoryChart = elvenCharts.getScoreHistoryChart( this.fellowship, this.$i18n, this.Highcharts, "2018-02-15");
                    this.tournamentHistoryChart = elvenCharts.getTournamentHistoryChart( this.fellowship, this.$i18n, this.Highcharts, "2018-03-12", this.members);

                }
                catch (err) {
                    console.error("Unable to get server name or set fellowship", err);
                }
                try {
                    this.fellowship.medianScore = this.members[Math.round(_.size(this.members) / 2)].currScore;
                }
                catch (err) {
                    console.log("Unable to determine median score!", err);
                }
                this.isInitialized = true;
                this.ajaxDone = true;
            },
            refreshFellowship: function () {
                let self = this;
                this.refreshingUsersInProgress = true;
                this.$ga.event('RefreshFellowship', 'Started', "", "");
                // axios.get(`/api/fellowship/${this.server}/${this.fellowshipId}/refresh`).then( (response) => {
                //     this.loadFellowshipData(response);
                // }).catch( (err) => {
                //     console.error("Failed to refresh fellowship");
                //     alert(err);
                // })
                var ws = new WebSocket(process.env.WEBSOCKET_API_URL);
                // event emmited when connected
                ws.onopen = function () {
                    console.log('websocket is connected ...')
                    // sending a send event to websocket server
                    try {
                        let data = JSON.stringify(
                            {server: self.server, fellowshipId: self.fellowshipId, method: "refreshFellowship"});
                        console.log("Sending WS Data: ", data);
                        self.refreshProgressLog = "Sending Request To ElvenStats Server";
                        ws.send(data);
                    }
                    catch (err) {
                        console.error("WS SEND ERROR", err);
                    }
                }
                // event emmited when receiving message
                ws.onmessage = function (ev) {
                    console.log(ev);
                    if (ev.data) {
                        try {
                            let data = JSON.parse(ev.data);
                            console.log("WS IS JSON: ", data);

                            if (data.type === "error") {
                                self.refreshUsersColor = "error";
                                self.refreshUsersIcon = "error";
                                if( data.code == "EEXPIRED" ) {
                                    self.refreshUsersHeadline = self.$i18n.t('site.status.realTimeUpdatesNotAvailableHeadline')
                                    self.refreshProgressLog = self.$i18n.t('site.status.realTimeUpdatesNotAvailableDescription'),
                                    self.refreshNeededHeadline = self.refreshUsersHeadline;
                                }else {
                                    self.refreshUsersHeadline = self.$i18n.t('site.status.errorOccurredImporting');
                                    self.refreshProgressLog = self.$i18n.t('site.status.errorOccurredImportingMessage');
                                    self.refreshNeededHeadline = self.refreshUsersHeadline + " " + self.$i18n.t('site.status.errorOccurredImportingWillNotBeAvailableInReports');
                                }

                            }
                            else {
                                if (data.percent) {
                                    self.refreshProgressValue = data.percent * 100;
                                    self.refreshProgressLog = data.message + "\n" + self.refreshProgressLog;
                                }
                                if (data.type === "finished") {
                                    self.loadFellowshipData(data);
                                    self.refreshUsersHeadline = self.$i18n.t('site.status.finishedSyncSuccessful');
                                    self.refreshUsersColor = "success";
                                    self.refreshUsersIcon = "check_circle";
                                    setTimeout(function () {
                                        self.refreshingUsersInProgress = false;
                                    }, 5000);
                                    ws.close();
                                    self.$ga.event('RefreshFellowship', 'Success', "", "");
                                    // Close socket.
                                }
                            }
                        }
                        catch (err) {
                            //self.refreshingUsersInProgress = false;
                            //self.refreshUsersColor = "error";
                            console.log(err);
                            //console.log("WS NOT JSON: " + ev.data);
                            //self.refreshProgressLog += ev.data + "\n";
                        }
                    }
                }

                ws.onclose = function (evt) {
                    console.log("Closed Socket");
                };

                ws.onerror = function (evt) {
                    self.refreshProgressLog +=
                        "WebSocket Error, your browser might not support websocket technology, please use Chrome.\n" +
                        self.refreshProgressLog;
                };
            },
            onCopy: function (e) {
                alert( this.$i18n.t('site.status.copyToClipboardSuccessful'));
                this.$ga.event('WeeklyReport', 'CopiedSuccess', "", 50);
            },
            onError: function (e) {
                alert(this.$i18n.t('site.status.copyToClipboardFailed'))
                this.$ga.event('WeeklyReport', 'CopiedFailed', "", 50);
            },
            getDirtyElements: function () {
                if (this.$refs && this.$refs.goodsTable && this.$refs.goodsTable.$children) {
                    return _.filter(this.$refs.goodsTable.$children, function (item) {
                        return item.compIdentifier === 'elv-user-editable-input' && item.isDirty === true;
                    });
                } else {
                    return [];
                }
            },
            saveGoodsTableChanges: function () {
                let self = this;
                _.each(this.getDirtyElements(), function (element) {
                    if (element.isInvalid) {
                        alert("You have an invalid value for one of your member's: " + element.fieldName +
                              " skipping this user.");
                    } else {
                        self.$ga.event('MemberGoods', 'Saved', "", 50);
                        self.saveDirtyElement(element);
                    }
                });
            },
            saveDirtyElement: function (item) {
                let self = this;
                var data = {
                    playerId: item.playerId,
                    server: this.server,
                    field: item.fieldName,
                    value: item.value,
                    updatedBy: this.viewAsPlayer && this.viewAsPlayer.username || "Unknown",
                    documentId: item._id,
                };
                axios.put(`/api/player/`, data).then((response) => {
                    if (response.data && response.data.status === "ok") {
                        item.isDirty = false;
                        item.originalValue = item.value;
                        self.isUnsavedItems = _.size(self.getDirtyElements()) > 0;
                    } else {
                        alert("Didn't save properly, report this to support.");
                        self.$ga.event('MemberGoods', 'SaveError', "", 0);
                    }
                }).catch((err) => {
                    alert("Unable to save: " + item.playerId + " value: " + item.value +
                          " this is most likely because you are trying to save with an invalid value.");
                    self.$ga.event('MemberGoods', 'SaveErrorInvalid', "", 0);
                });
            }
        }
    }

    function hcFormatGood(number) {
        if (!number) { return "ERROR";}
        return (number / 1000).toFixed(0) + "k";
    }

    function createColumnOptions(data, tierNumber, i18n, highCharts) {

        try {
            //console.log("moment locale in createColumn", moment.locale());
            let temp = getHighchartLanguageOptions();
            //console.log(temp);
            highCharts.setOptions( temp );
        }catch(err) {
            console.log("Failed to set highcharts options", err);
        }

        var opts = {
            chart: {
                type: "column",
                plotBackgroundColor: null,
                plotBorderWidth: 0,
                plotShadow: false,
                // width: 450,
                height: 250,
                spacingTop: 0,
                spacingBottom: 0,
                spacingLeft: 0,
                spacingRight: 0,
                events: {
                    drilldown: function (options) {
                        // console.log("drill down event fired");

                        //this.xAxis.labels.formatter = function() { return this.value; }
                        //this.xAxis.labels.useHTML = false;
                        this.update({xAxis: {labels: {useHTML: false}}});
                    },
                    drillup: function () {
                        //console.log("drill up event fired");
                        this.update({xAxis: {labels: {useHTML: true}}});
                    }
                }
            },
            lang: {
                thousandsSep: ','
            },
            legend: {
                enabled: true,
            },
            yAxis: {
                title: "",
            },
            xAxis: {
                type: 'category',
                labels: {
                    formatter: function () {
                        var boosts = ['marble', 'steel', 'planks', 'crystal', 'scrolls', 'silk', 'elixir', 'magic_dust', 'elixir',
                                      'gems'];
                        if (-1 === boosts.indexOf(this.value)) {
                            return "<span>" + this.value + "</span>";
                        }
                        return "<img src='/static/img/icons/" + this.value + ".png'>";
                        //return "<img src='/static/img/icons/marble.png'>";
                    },
                    useHTML: true,
                }
            },
            title: {
                enabled: false,
                text: "",
                //text: 'T' + tierNumber,
                align: 'center',
                verticalAlign: 'middle',
                y: 75
            },
            subtitle: {
                text: i18n.t('site.fellowship.barCharts.clickSlice'),
            },
            tooltip: {
                formatter: function () {
                    var str = `${ i18n.t('site.fellowship.popCultureTable.total') }: <strong>` + hcFormatGood(this.point.y) +
                              "</strong>";
                    str += this.point.memberCount ? `<br/> ${ i18n.t('site.fellowship.stats.memberCount') }: <strong>${this.point.memberCount}</strong>` : "";
                    return str;
                }
                // pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>'
            },
            animation: {
                duration: 1000
            },
            credits: {
                enabled: false,
                position: {
                    align: 'center',
                    verticalAlign: 'bottom',
                    x: 10,
                    y: -10
                }
            },
            plotOptions: {
                series: {
                    borderWidth: 5,
                    dataLabels: {
                        enabled: true,
                        // format: '{point.y:,.0f}'
                        //  useHTML: true,
                        formatter: function () {
                            let display = (this.y / 1000).toFixed(0) + "k";
                            // if( this.y > 1000000 ) {
                            //     display = (this.y/1000000).toFixed(2) + "M";
                            // }

                            if (this.point && this.point.percent) {
                                return "<center><span style=\"text-align:center; color: #5B5B5B; font-family: 'Lato', 'Open Sans', Helvetica, sans-serif !important;\">" +
                                       display + "<br/> " + ((this.point && this.point.percent + "%") || "") + "</span></center>";
                            } else {
                                return "<center><span style=\"text-align:center; color: #5B5B5B; font-family: 'Lato', 'Open Sans', Helvetica, sans-serif !important;\">" +
                                       display + "</span></center>";
                            }
                        }
                    }
                }
            },
            responsive: {
                rules: [{
                    condition: {
                        maxWidth: 1000
                    },
                    chartOptions: {
                        legend: {
                            layout: 'horizontal',
                            align: 'center',
                            verticalAlign: 'bottom'
                        }
                    }
                }]
            },
            series: [{
                type: 'column',
                name: 'Tier',
                innerSize: '50%',
                data: []
            }],

        };


        let tierLabel = i18n.t('site.fellowship.barCharts.tier1');
        if( tierNumber == 2 ) { tierLabel = i18n.t('site.fellowship.barCharts.tier2'); }
        if( tierNumber == 3 ) { tierLabel = i18n.t('site.fellowship.barCharts.tier3'); }
        opts.series[0].name = tierLabel;
        opts.series[0].data = _.map(data[tierNumber], function (item) {
            return {
                name: item.name, percent: item.percent, memberCount: item.memberCount, y: item.total, color: elvenCharts.getColorFromGoodType(
                    item.name), drilldown: item.name
            };
        });
        opts.drilldown = {
            drillUpButton: {position: {y: -28, verticalAlign: "top", x: -5}},
            labels: {
                formatter: function () {
                    return this.value;
                },
                useHTML: false,
            },
            series: _.map(data[tierNumber], function (boost) {
                return {name: i18n.t('site.common.' + boost.name), id: boost.name, data: boost.members};
            })
        };

        //console.log(JSON.stringify(opts,null,3) );
        return opts;
    }

    // function createPieOptions(data, tierNumber) {
    //     var boosts = ["marble", "steel", "planks", "crystal", "scrolls", "elixir", "magic_dust", "gems"];
    //     var opts = {
    //         chart: {
    //             type: "pie",
    //             plotBackgroundColor: null,
    //             plotBorderWidth: 0,
    //             plotShadow: false,
    //             width: 350,
    //             height: 250,
    //             spacingTop: 0,
    //             spacingBottom: 0,
    //             spacingLeft: 0,
    //             spacingRight: 0
    //         },
    //         title: {
    //             text: 'T' + tierNumber,
    //             align: 'center',
    //             verticalAlign: 'middle',
    //             y: 75
    //         },
    //         subtitle: {
    //             text: 'Click a slice for details (by member)"'
    //         },
    //         tooltip: {
    //             pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>'
    //         },
    //         animation: {
    //             duration: 1000
    //         },
    //         credits: {
    //             enabled: false,
    //             position: {
    //                 align: 'center',
    //                 verticalAlign: 'bottom',
    //                 x: 10,
    //                 y: -10
    //             }
    //         },
    //         plotOptions: {
    //             pie: {
    //                 // /size: '100%',
    //                 dataLabels: {
    //                     enabled: true,
    //                     distance: -10,
    //                     style: {
    //                         fontWeight: 'bold',
    //                         color: 'white'
    //                     }
    //                 },
    //                 startAngle: -90,
    //                 endAngle: 90,
    //                 center: ['50%', '75%']
    //             }
    //         },
    //         series: [{
    //             type: 'pie',
    //             name: 'Tier',
    //             innerSize: '50%',
    //             data: []
    //         }]
    //     };
    //
    //     opts.series[0].name = "Tier " + tierNumber;
    //     opts.series[0].data = _.map(data[tierNumber], function (item) {
    //         return {
    //             name: item.name, y: item.total, percent: item.percent, color: getColorFromGoodType(
    //                 item.name), drilldown: item.name
    //         };
    //     });
    //     opts.drilldown = {
    //         series: _.map(data[tierNumber], function (boost) {
    //             return {name: boost.name, id: boost.name, data: boost.members};
    //         })
    //     };
    //
    //     //console.log(JSON.stringify(opts,null,3) );
    //     return opts;
    // }



</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
    .red {
        color: red;
        background-color: white !important;
        font-weight: bold;
    }
    .darkred {
        color: darkred;
    }
    .subtitle {
        margin-top: -13px;
    }

    .highcharts-container {
        margin: 0 auto;
    }

    table.table tbody td:first-child,table.table tbody td:not(:first-child),table.table tbody th:first-child,table.table tbody th:not(:first-child),table.table thead td:first-child,table.table thead td:not(:first-child),table.table thead th:first-child,table.table thead th:not(:first-child) {
        padding: 0 12px
    }

    table.table tfoot tr td {
        padding: 0 12px
    }

    table.table tbody td:first-child, table.table tbody td:not(:first-child), table.table tbody th:first-child, table.table tbody th:not(:first-child), table.table thead td:first-child, table.table thead td:not(:first-child), table.table thead th:first-child, table.table thead th:not(:first-child) {
        padding-left:12px;
        padding-right:12px;
    }

    .mountainHallTh {
        width: 75px;
        max-width: 75px;
        no-wrap: unset;
        white-space: normal !important;
    }

    .colPerDayTh {
        width: 125px;
        max-width: 125px;
        white-space: normal !important;
        no-wrap: unset;
    }

    input[type="text"] {
        text-align: right;
        padding-right: 4px;
        border: 1px solid #DDDDDD;
    }

    th .tooltip {
        position: unset;
        font-family: unset;
        font-size: unset;
        font-style: unset;
        font-weight: unset;
        line-height: unset;
        text-align: unset;
        text-align: left;
        display: unset;
        /* text-decoration:none; */
        /* text-shadow:none; */
        text-transform: uppercase;
        letter-spacing: unset;
        word-break: unset;
        word-spacing: unset;
        word-wrap: unset;

        filter: unset;
        line-break: unset;
        opacity: unset;
        /* line-break:auto; */
    }
</style>



// WEBPACK FOOTER //
// src/views/FellowshipDetail.vue